/** Multilingual text helpers — single source of truth for language lists
 *  and for packing/unpacking { lang: text } values.
 */

// Editor languages: international + all 22 scheduled Indian languages (+ Bhojpuri).
export const LANGUAGES = [
  // International
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
  { code: "nl", label: "Nederlands" },
  { code: "sv", label: "Svenska" },
  { code: "no", label: "Norsk", name: "Norwegian" },
  { code: "da", label: "Dansk", name: "Danish" },
  { code: "fi", label: "Suomi", name: "Finnish" },
  { code: "pl", label: "Polski" },
  { code: "cs", label: "Čeština", name: "Czech" },
  { code: "el", label: "Ελληνικά", name: "Greek" },
  { code: "ru", label: "Русский", name: "Russian" },
  { code: "uk", label: "Українська", name: "Ukrainian" },
  { code: "tr", label: "Türkçe" },
  { code: "ar", label: "العربية", name: "Arabic" },
  { code: "fa", label: "فارسی", name: "Persian" },
  { code: "he", label: "עברית", name: "Hebrew" },
  { code: "zh-CN", label: "简体中文", name: "Chinese Simplified" },
  { code: "zh-TW", label: "繁體中文", name: "Chinese Traditional" },
  { code: "ja", label: "日本語", name: "Japanese" },
  { code: "ko", label: "한국어", name: "Korean" },
  { code: "vi", label: "Tiếng Việt", name: "Vietnamese" },
  { code: "th", label: "ไทย", name: "Thai" },
  { code: "id", label: "Bahasa Indonesia" },
  { code: "ms", label: "Bahasa Melayu" },
  { code: "fil", label: "Filipino", name: "Filipino" },
  { code: "sw", label: "Kiswahili", name: "Swahili" },
  { code: "af", label: "Afrikaans" },
  // Indian languages — all 22 scheduled languages, plus Bhojpuri
  { code: "hi", label: "हिन्दी", name: "Hindi" },
  { code: "bn", label: "বাংলা", name: "Bengali" },
  { code: "te", label: "తెలుగు", name: "Telugu" },
  { code: "mr", label: "मराठी", name: "Marathi" },
  { code: "ta", label: "தமிழ்", name: "Tamil" },
  { code: "ur", label: "اردو", name: "Urdu" },
  { code: "gu", label: "ગુજરાતી", name: "Gujarati" },
  { code: "kn", label: "ಕನ್ನಡ", name: "Kannada" },
  { code: "or", label: "ଓଡ଼ିଆ", name: "Odia" },
  { code: "ml", label: "മലയാളം", name: "Malayalam" },
  { code: "pa", label: "ਪੰਜਾਬੀ", name: "Punjabi" },
  { code: "as", label: "অসমীয়া", name: "Assamese" },
  { code: "mai", label: "मैथिली", name: "Maithili" },
  { code: "sat", label: "ᱥᱟᱱᱛᱟᱲᱤ", name: "Santali" },
  { code: "ks", label: "كٲشُر", name: "Kashmiri" },
  { code: "ne", label: "नेपाली", name: "Nepali" },
  { code: "gom", label: "कोंकणी", name: "Konkani" },
  { code: "sd", label: "سنڌي", name: "Sindhi" },
  { code: "doi", label: "डोगरी", name: "Dogri" },
  { code: "mni", label: "মৈতৈলোন্", name: "Manipuri" },
  { code: "brx", label: "बर'", name: "Bodo" },
  { code: "sa", label: "संस्कृतम्", name: "Sanskrit" },
  { code: "bho", label: "भोजपुरी", name: "Bhojpuri" },
];

// Subset the Google Translate widget reliably supports (page translation).
export const GT_LANGUAGES = LANGUAGES.filter((l) => !["sat", "ks", "brx"].includes(l.code));

export const LANG_LABELS = Object.fromEntries(LANGUAGES.map((l) => [l.code, l.label]));

/** Normalise a stored value into [code, text] pairs (plain strings count as English). */
export function mlEntries(value) {
  if (!value) return [];
  if (typeof value === "string") {
    return value.trim() ? [["en", value]] : [];
  }
  if (typeof value === "object") {
    return Object.entries(value).filter(([, v]) => typeof v === "string" && v.trim());
  }
  return [];
}

/** Best single-string rendering — English when present, else the first written language. */
export function mlPrimary(value) {
  const e = mlEntries(value);
  if (!e.length) return "";
  const en = e.find(([k]) => k === "en");
  return (en || e[0])[1];
}

export function hasMlContent(value) {
  return mlEntries(value).length > 0;
}

/** Prepare an editor value for submission: trim, drop empties, collapse single-en to a string. */
export function packMl(value) {
  const entries = mlEntries(value).map(([k, v]) => [k, v.trim()]);
  if (!entries.length) return "";
  if (entries.length === 1 && entries[0][0] === "en") return entries[0][1];
  return Object.fromEntries(entries);
}
